import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import API from "../../../utils/API";
import Loader from "../../Loader";
import { FaCheckCircle, FaInfoCircle } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useThemeContext } from "../../../context/ThemeContext";
import DynamicHeader from "../../DynamicComponents/DynamicHeader";

const ExitSurvey = () => {
  const user = useSelector((state) => state.auth.user) || {};
  const { roll_no, program_code } = user;
  const { darkMode } = useThemeContext();
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState("");

  // Fetch survey questions
  const fetchQuestions = async () => {
    setIsLoading(true);
    try {
      const response = await API.get(
        `ece/student/exit-survey/questions?user_id=${roll_no}&program_code=${program_code}`
      );

      if (response.data.message) {
        // Survey already submitted
        setAlreadySubmitted(true);
        setSubmissionMessage(response.data.message);
      } else {
        setQuestions(response.data.questions || []);
        // Initialize responses with empty values
        const initialResponses = {};
        response.data.questions.forEach((q) => {
          initialResponses[q.question_number] = 0;
        });
        setResponses(initialResponses);
      }
    } catch (error) {
      toast.error("Failed to load survey questions");
      console.error("Error fetching questions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle radio button change
  const handleResponseChange = (questionNumber, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionNumber]: parseInt(value),
    }));
  };

  // Submit survey responses
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all questions are answered
    const unanswered = Object.values(responses).some((r) => r === 0);
    if (unanswered) {
      toast.error("Please answer all questions before submitting");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        roll_no,
        program_code: program_code,
        responses,
      };

      await API.post(
        `ece/student/exit-survey/submit?user_id=${roll_no}&program_code=${program_code}`,
        payload
      );

      toast.success("Survey submitted successfully!");
      setAlreadySubmitted(true);
      setSubmissionMessage("Thank you for completing the exit survey!");
    } catch (error) {
      toast.error("Failed to submit survey");
      console.error("Error submitting survey:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load questions on component mount
  useEffect(() => {
    if (roll_no) {
      fetchQuestions();
    }
  }, [roll_no, program_code]);

  return (
    <div
      className="min-h-screen pb-12"
      style={{ backgroundColor: darkMode ? "#1E1E2E" : "#F8F8F8" }}
    >
      <DynamicHeader />
      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div
          className="mx-auto max-w-4xl rounded-xl shadow-lg overflow-hidden"
          style={{
            backgroundColor: darkMode ? "#0D1117" : "#FFFFFF",
            border: darkMode ? "1px solid #252525" : "1px solid #E5E5E5",
            boxShadow: darkMode
              ? "0 4px 12px rgba(0, 0, 0, 0.5)"
              : "0 4px 12px rgba(0, 0, 0, 0.08)",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 py-5 border-b"
            style={{
              borderColor: darkMode ? "#252525" : "#E5E5E5",
              backgroundColor: darkMode ? "#161B22" : "#F9FAFB",
            }}
          >
            <h1
              className="text-2xl font-bold"
              style={{ color: darkMode ? "#FFFFFF" : "#A41E22" }}
            >
              Program Exit Survey
            </h1>
            <p
              className="mt-1 text-sm"
              style={{ color: darkMode ? "#B0B3B8" : "#666666" }}
            >
              Please rate your achievement for each program outcome on a scale
              of 1 (Lowest) to 5 (Highest)
            </p>
          </div>

          {/* Content Section */}
          <div className="p-6 sm:p-8">
            {alreadySubmitted ? (
              <div
                className="flex items-start p-4 rounded-lg mb-6 space-x-4"
                style={{
                  backgroundColor: darkMode ? "#252525" : "#F5F5F5",
                  border: darkMode ? "1px solid #383838" : "1px solid #E0E0E0",
                }}
              >
                <div
                  className="flex-shrink-0 p-2 rounded-full"
                  style={{ backgroundColor: "#E8F5E9" }}
                >
                  <FaCheckCircle className="text-green-600 text-lg" />
                </div>
                <div
                  className="text-justify leading-relaxed"
                  style={{
                    color: darkMode ? "#D1D5DB" : "#4B5563",
                    fontSize: "0.875rem",
                  }}
                >
                  {submissionMessage}
                </div>
              </div>
            ) : isLoading ? (
              <div className="flex justify-center items-center py-16">
                <Loader />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="space-y-6">
                  {questions.map((question) => (
                    <div
                      key={question.id}
                      className="p-5 rounded-lg transition-all hover:shadow-sm"
                      style={{
                        backgroundColor: darkMode ? "#161B22" : "#F9FAFB",
                        border: darkMode
                          ? "1px solid #252525"
                          : "1px solid #E5E7EB",
                      }}
                    >
                      <h3
                        className="font-medium text-md mb-4"
                        style={{ color: darkMode ? "#FFFFFF" : "#1F2937" }}
                      >
                        <span
                          className="inline-block mr-2 font-bold"
                          style={{ color: "#A41E22" }}
                        >
                          {question.question_number}.
                        </span>
                        {question.question_text}
                      </h3>

                      {/* Rating Scale */}
                      <div className="mt-4">
                        <div className="flex justify-between items-center mb-3">
                          {[1, 2, 3, 4, 5].map((value) => (
                            <label
                              key={value}
                              className="flex flex-col items-center group cursor-pointer"
                            >
                              <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                  responses[question.question_number] === value
                                    ? "bg-[#A41E22] text-white"
                                    : darkMode
                                      ? "bg-[#252525] hover:bg-[#30363d]"
                                      : "bg-gray-100 hover:bg-gray-200"
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`question-${question.question_number}`}
                                  value={value}
                                  checked={
                                    responses[question.question_number] ===
                                    value
                                  }
                                  onChange={() =>
                                    handleResponseChange(
                                      question.question_number,
                                      value
                                    )
                                  }
                                  className="sr-only"
                                />
                                <span className="font-medium">{value}</span>
                              </div>
                              <span
                                className={`text-xs mt-1 ${
                                  responses[question.question_number] === value
                                    ? "font-bold"
                                    : ""
                                }`}
                                style={{
                                  color: darkMode ? "#B0B3B8" : "#4B5563",
                                }}
                              >
                                {value === 1 && "Lowest"}
                                {value === 5 && "Highest"}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Submit Button */}
                <div className="mt-8 text-center">
                  <button
                    type="submit"
                    className="px-8 py-3 rounded-lg font-medium transition-all transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#A41E22] focus:ring-opacity-50"
                    style={{
                      backgroundColor: "#A41E22",
                      color: "#FFFFFF",
                      boxShadow: "0 4px 6px rgba(164, 30, 34, 0.2)",
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      "Submit Survey"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <Toaster
        toastOptions={{
          duration: 3000,
          style: {
            background: darkMode ? "#121212" : "#FFFFFF",
            color: darkMode ? "#FFFFFF" : "#222222",
            border: darkMode ? "1px solid #252525" : "1px solid #E5E5E5",
            boxShadow: darkMode
              ? "0 4px 12px rgba(0, 0, 0, 0.5)"
              : "0 4px 12px rgba(0, 0, 0, 0.1)",
          },
        }}
      />
    </div>
  );
};

export default ExitSurvey;
